import { useEffect, useId, useRef, useState } from "react";
import { BOARD_FONTS, fontOfStyle, type BoardFont } from "../../editor/textFonts";

/**
 * Шрифт надписи: кнопка с названием текущего шрифта и список, где каждое
 * название написано своим шрифтом — как в Miro: видно, что выбираешь.
 */
export function TextFontPicker({
  fontFamily,
  disabled,
  onPick,
}: {
  fontFamily: string | undefined;
  disabled?: boolean;
  onPick: (font: BoardFont) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = fontOfStyle(fontFamily);

  useEffect(() => {
    if (!open) return;
    const onDown = (ev: PointerEvent) => {
      if (!rootRef.current?.contains(ev.target as Node)) setOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div className="wb-font-picker" ref={rootRef}>
      <button
        type="button"
        className="wb-font-picker__btn"
        title="Шрифт"
        aria-label={`Шрифт: ${current.family}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        style={{ fontFamily: current.css }}
        onClick={() => setOpen((v) => !v)}
      >
        {current.family}
        <span className="wb-font-picker__caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <ul className="wb-font-picker__list" role="listbox" aria-label="Шрифт">
          {BOARD_FONTS.map((f) => (
            <li key={f.family}>
              <button
                type="button"
                role="option"
                aria-selected={f.family === current.family}
                className={`wb-font-picker__item${f.family === current.family ? " is-active" : ""}`}
                style={{ fontFamily: f.css }}
                onClick={() => {
                  setOpen(false);
                  onPick(f);
                }}
              >
                {f.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Размер шрифта: можно вписать любое число, а можно выбрать из готовых —
 * список выпадает у самого поля. Применяется по Enter или когда уходишь
 * из поля; Escape возвращает прежнее значение.
 */
export function TextSizeField({
  value,
  presets,
  min,
  max,
  disabled,
  onApply,
}: {
  value: number;
  presets: readonly number[];
  min: number;
  max: number;
  disabled?: boolean;
  onApply: (size: number) => void;
}) {
  const listId = useId();
  const [draft, setDraft] = useState(String(value));
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const focusedRef = useRef(false);
  /**
   * Что уже применили. Enter применяет, потом фокус уходит из поля — и без
   * этой памяти тот же размер применился бы второй раз.
   */
  const appliedRef = useRef<number | null>(null);
  const apply = (size: number) => {
    if (appliedRef.current === size) return;
    appliedRef.current = size;
    onApply(size);
  };

  // Значение поменялось снаружи (за угол, кнопками ±) — показываем его,
  // если человек сейчас не печатает в поле.
  useEffect(() => {
    appliedRef.current = null;
    if (!focusedRef.current) setDraft(String(value));
  }, [value]);

  // Список закрывается нажатием мимо него и Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (ev: PointerEvent) => {
      if (!rootRef.current?.contains(ev.target as Node)) setOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const commit = () => {
    const n = Number(draft.replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      setDraft(String(value));
      return;
    }
    const size = Math.min(max, Math.max(min, Math.round(n * 10) / 10));
    setDraft(String(size));
    if (size !== value) apply(size);
  };

  return (
    <span className="wb-size-field" ref={rootRef}>
      <input
        className="wb-selection-toolbar__font-size-input"
        aria-label="Размер шрифта"
        inputMode="decimal"
        disabled={disabled}
        value={draft}
        onFocus={(e) => {
          focusedRef.current = true;
          appliedRef.current = null;
          e.currentTarget.select();
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          focusedRef.current = false;
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
            e.currentTarget.blur();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setDraft(String(value));
            e.currentTarget.blur();
          }
        }}
      />
      {/* Свой список, а не встроенный в браузер (datalist): тот показывал
          только размеры, начинающиеся с уже вписанного, — при «18» в поле
          он был пустым и не открывался вовсе. Кнопки фокус не забирают,
          поэтому выделение в надписи остаётся и размер ложится на него. */}
      <button
        type="button"
        className="wb-size-field__toggle"
        title="Готовые размеры"
        aria-label="Готовые размеры"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        ▾
      </button>
      {open ? (
        <ul className="wb-size-field__list" id={listId} role="listbox" aria-label="Размер шрифта">
          {presets.map((s) => (
            <li key={s}>
              <button
                type="button"
                role="option"
                aria-selected={s === value}
                className={`wb-size-field__item${s === value ? " is-active" : ""}`}
                onClick={() => {
                  setOpen(false);
                  setDraft(String(s));
                  appliedRef.current = null;
                  apply(s);
                }}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}
